import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';

const PmTable = ({
  columns = [],
  data = [],
  onSort,
  sortConfig = { key: null, direction: 'asc' },
  onToggleColumn,
  onReorderColumns,
  onResetColumns,
  formatCellValue,
  renderActions,
  isLoading = false,
  loadingMessage = 'Loading...',
  emptyMessage = 'No data',
  errorMessage = 'An error occurred',
  onRetry,
  pageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  className = '',
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(pageSize);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [columnSearchTerm, setColumnSearchTerm] = useState('');
  const columnSettingsRef = useRef(null);
  const columnSettingsButtonRef = useRef(null);

  // Reset page when data or pageSize changes
  useEffect(() => {
    setCurrentPage(1);
  }, [data.length, internalPageSize]);

  // Reset page size when prop changes
  useEffect(() => {
    setInternalPageSize(pageSize);
  }, [pageSize]);

  // Handle click outside column settings panel
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showColumnSettings &&
        columnSettingsRef.current &&
        !columnSettingsRef.current.contains(event.target) &&
        columnSettingsButtonRef.current &&
        !columnSettingsButtonRef.current.contains(event.target)
      ) {
        setShowColumnSettings(false);
        setColumnSearchTerm('');
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && showColumnSettings) {
        setShowColumnSettings(false);
        setColumnSearchTerm('');
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showColumnSettings]);

  // Calculate pagination
  const totalPages = useMemo(() => {
    return Math.ceil(data.length / internalPageSize) || 1;
  }, [data.length, internalPageSize]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * internalPageSize;
    const endIndex = startIndex + internalPageSize;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, internalPageSize]);

  // Filter and order columns based on search and visibility
  const filteredColumns = useMemo(() => {
    let filtered = columns.filter(col => col.visible !== false);
    
    if (columnSearchTerm) {
      const search = columnSearchTerm.toLowerCase();
      filtered = filtered.filter(col => 
        col.label.toLowerCase().includes(search) ||
        col.key.toLowerCase().includes(search)
      );
    }
    
    return filtered;
  }, [columns, columnSearchTerm]);

  // Get hidden columns for the indicator button
  const hiddenColumns = useMemo(() => {
    return columns.filter(col => col.visible === false);
  }, [columns]);

  // Handle page size change
  const handlePageSizeChange = useCallback((e) => {
    const newSize = Number(e.target.value);
    setInternalPageSize(newSize);
    setCurrentPage(1);
  }, []);

  // Handle page navigation
  const handlePreviousPage = useCallback(() => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  }, [totalPages]);

  // Handle column visibility toggle
  const handleToggleColumn = useCallback((key) => {
    onToggleColumn?.(key);
  }, [onToggleColumn]);

  // Handle column reorder
  const handleMoveColumnUp = useCallback((key) => {
    const orderedKeys = columns.map(col => col.key);
    const currentIndex = orderedKeys.indexOf(key);
    if (currentIndex > 0) {
      const newOrderedKeys = [...orderedKeys];
      [newOrderedKeys[currentIndex - 1], newOrderedKeys[currentIndex]] = 
        [newOrderedKeys[currentIndex], newOrderedKeys[currentIndex - 1]];
      onReorderColumns?.(newOrderedKeys);
    }
  }, [columns, onReorderColumns]);

  const handleMoveColumnDown = useCallback((key) => {
    const orderedKeys = columns.map(col => col.key);
    const currentIndex = orderedKeys.indexOf(key);
    if (currentIndex < orderedKeys.length - 1) {
      const newOrderedKeys = [...orderedKeys];
      [newOrderedKeys[currentIndex], newOrderedKeys[currentIndex + 1]] = 
        [newOrderedKeys[currentIndex + 1], newOrderedKeys[currentIndex]];
      onReorderColumns?.(newOrderedKeys);
    }
  }, [columns, onReorderColumns]);

  // Handle sort
  const handleSort = useCallback((key) => {
    onSort?.(key);
  }, [onSort]);

  // Format cell value
  const formatValue = useCallback((key, value, row) => {
    if (formatCellValue) {
      return formatCellValue(key, value, row);
    }
    if (value === null || value === undefined) {
      return '-';
    }
    return String(value);
  }, [formatCellValue]);

  // Get sort direction indicator
  const getSortIndicator = useCallback((key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    }
    return null;
  }, [sortConfig]);

  // Render loading state
  if (isLoading) {
    return (
      <div className="pm-table-wrap">
        <article className="pm-empty-state" aria-busy="true">
          <div className="pm-empty-state-icon" aria-hidden="true">⏳</div>
          <h3>{loadingMessage}</h3>
        </article>
      </div>
    );
  }

  // Render error state
  if (errorMessage && data.length === 0) {
    return (
      <div className="pm-table-wrap">
        <div className="pm-empty-state">
          <div className="pm-empty-state-icon" aria-hidden="true">⚠️</div>
          <h3>Error</h3>
          <p>{errorMessage}</p>
          {onRetry && (
            <button className="pm-btn-primary" onClick={onRetry}>
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // Render empty state
  if (data.length === 0) {
    return (
      <div className="pm-table-wrap">
        <div className="pm-empty-state">
          <div className="pm-empty-state-icon" aria-hidden="true">📦</div>
          <h3>{emptyMessage}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className={`pm-table-wrap ${className}`.trim()}>
      {/* Column settings button — always visible for reorder/visibility controls */}
      <button
        ref={columnSettingsButtonRef}
        className="pm-column-settings-btn"
        onClick={() => setShowColumnSettings(!showColumnSettings)}
        title="Configure columns"
      >
        ⚙ Columns{hiddenColumns.length > 0 ? ` (${hiddenColumns.length} hidden)` : ''}
      </button>

      {/* Column settings panel */}
      {showColumnSettings && (
        <div ref={columnSettingsRef} className="pm-column-settings-panel">
          <div className="pm-column-settings-header">
            <h3>Column Settings</h3>
            <button
              className="pm-column-settings-close"
              onClick={() => {
                setShowColumnSettings(false);
                setColumnSearchTerm('');
              }}
              aria-label="Close column settings"
            >
              ✕
            </button>
          </div>
          
          <div className="pm-column-settings-search">
            <input
              type="search"
              placeholder="Search columns..."
              value={columnSearchTerm}
              onChange={(e) => setColumnSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="pm-column-settings-list">
            {filteredColumns.length === 0 ? (
              <div className="pm-column-settings-empty">
                No columns found
              </div>
            ) : (
              filteredColumns.map((col, index) => {
                const orderedKeys = columns.map(c => c.key);
                const currentIndex = orderedKeys.indexOf(col.key);
                const isFirst = currentIndex === 0;
                const isLast = currentIndex === orderedKeys.length - 1;
                
                return (
                  <div key={col.key} className="pm-column-settings-item">
                    <div className="pm-column-settings-drag-handle" title="Drag to reorder">
                      ⠿
                    </div>
                    <div className="pm-column-settings-label">
                      {col.label}
                    </div>
                    <label className="pm-column-settings-toggle">
                      <input
                        type="checkbox"
                        checked={col.visible !== false}
                        onChange={() => handleToggleColumn(col.key)}
                      />
                      <span>Visible</span>
                    </label>
                    <div className="pm-column-settings-move">
                      <button
                        className="pm-column-settings-move-btn"
                        onClick={() => handleMoveColumnUp(col.key)}
                        disabled={isFirst}
                        title="Move up"
                      >
                        ▲
                      </button>
                      <button
                        className="pm-column-settings-move-btn"
                        onClick={() => handleMoveColumnDown(col.key)}
                        disabled={isLast}
                        title="Move down"
                      >
                        ▼
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          <div className="pm-column-settings-footer">
            <button
              className="pm-column-settings-reset"
              onClick={() => {
                onResetColumns?.();
                setShowColumnSettings(false);
              }}
            >
              Reset to Defaults
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <table className="pm-table" role="grid">
        <thead>
          <tr>
            {/* Actions column (if renderActions is provided) */}
            {renderActions && (
              <th key="actions" style={{ width: '100px' }}>
                <div className="pm-header-content">
                  <div className="pm-header-label">Actions</div>
                </div>
              </th>
            )}
            
            {/* Data columns */}
            {filteredColumns.map((col) => (
              <th
                key={col.key}
                className="pm-sortable-header"
                onClick={() => handleSort(col.key)}
              >
                <div className="pm-header-content">
                  <div className="pm-header-controls">
                    {/* Column move controls (visual only as per spec) */}
                    <div className="pm-column-move-placeholder" title="Drag to reorder">
                      ⠿
                    </div>
                  </div>
                  <div className="pm-header-label">
                    {col.label}
                    {getSortIndicator(col.key)}
                  </div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {/* Actions cell (if renderActions is provided) */}
              {renderActions && (
                <td key="actions">
                  <div className="pm-row-actions">
                    {renderActions(row)}
                  </div>
                </td>
              )}
              
              {/* Data cells */}
              {filteredColumns.map((col) => (
                <td key={col.key}>
                  {formatValue(col.key, row[col.key], row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      {data.length > 0 && (
        <div className="pm-pagination">
          <div className="pm-pagination-left">
            <label htmlFor="page-size-select">Rows per page:</label>
            <select
              id="page-size-select"
              value={internalPageSize}
              onChange={handlePageSizeChange}
              className="pm-pagination-select"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          
          <div className="pm-pagination-center">
            <span className="pm-pagination-range">
              {(currentPage - 1) * internalPageSize + 1}–
              {Math.min(currentPage * internalPageSize, data.length)} of {data.length}
            </span>
          </div>
          
          <div className="pm-pagination-right">
            <button
              className={`pm-pagination-btn ${currentPage === 1 ? 'disabled' : ''}`.trim()}
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              ◀ Prev
            </button>
            <button
              className={`pm-pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`.trim()}
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              Next ▶
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PmTable;